import { Route, ForbiddenError, FromBody, HttpStatusCodes, NotFoundError, Result, FromQuery, Inject } from '@resourceful-hono/core';
import { Permissions } from '../../../../services/OAuth/enums.ts';
import { BaseResource } from '../../BaseResource.ts';
import { Protected } from '../../common/decorators.ts';
import { DEFAULT_LIMIT, DEFAULT_ORDER, GET_SCHEMA, POST_SCHEMA } from './schema.ts';
import type { z } from 'zod';
import { Account, CashTransactionRequest } from '../../../../entities/index.ts';
import { UnprocessableContentError } from '../../common/errors.ts';
import { Game } from '../../../../entities/Game.ts';
import { GameEvent } from '../../common/events.ts';
import { SystemAccountsResource } from '../../index.ts';
import { EventService } from '../../../../services/Events.ts';

@Route('transactions/requests')
export class TransactionRequestsResource extends BaseResource {
  @Inject()
  declare private readonly gameEvents: EventService;

  @Protected([Permissions.GetTransactionRequest])
  public async GET(
    @FromQuery(GET_SCHEMA) query: z.infer<typeof GET_SCHEMA>
  ) {
    const game = await Game.findOne({
      where: {
        players: {
          id: this.userId,
        },
      },
    });
  
    if (!game)
      throw new ForbiddenError('You need to be in a game.');
  
    const {
      take = DEFAULT_LIMIT,
      order = DEFAULT_ORDER,
    } = query;
    const transactionRequests = await CashTransactionRequest.findAndCount({
      where: {
        ...query.where,
        game: {
          id: game.id,
        },
      },
      relations: {
        creditor: {
          owner: true,
        },
        debitor: {
          owner: true,
        },
        responses: {
          user: true,
        },
        reviewers: true,
      },
      order,
      take,
    });
  
    return Result(HttpStatusCodes.Ok, transactionRequests);
  }

  @Protected([Permissions.PostTransactionRequest])
  public async POST(@FromBody(POST_SCHEMA) body: z.infer<typeof POST_SCHEMA>) {
    const user = await this.user;

    const game = await Game.findOne({
      where: {
        id: user.game?.id,
      },
      relations: {
        players: true,
      },
    });

    if (!game)
      throw new UnprocessableContentError('You need to be in a game to create transaction requests.');

    const to = await Account.findOne({
      where: {
        owner: {
          id: this.userId,
        },
      },
    });

    if (!to) throw new ForbiddenError();

    const from = await Account.findOne({
      where: {
        id: body.from,
      },
      relations: {
        owner: true,
      },
    });

    if (!from)
      throw new NotFoundError(
        `Debit account with ID ${body.from} does not exist`
      );

    const cashTransactionRequest = CashTransactionRequest.create({
      game,
      amount: body.amount,
      note: body.note,
      debitor: from,
      creditor: to,
      responses: [],
    });

    if (from.type === 'system')
      cashTransactionRequest.reviewers = game
        .players
        .filter(player => player.id !== user.id);
    else {
      if (!from.owner) throw new Error('User account has no owner');
      cashTransactionRequest.reviewers = [from.owner];
    }

    await cashTransactionRequest.save();

    // publish to client
    this.gameEvents.dispatchEvent(
      new GameEvent({
        gameId: game.id,
        target: this.route.split('/'),
      })
    );
    
    // we need live updates for when users interact with system accounts
    if (from.type === 'system' && from.systemKey)
      this.sendSystemAccountInvalidation(game.id, from.systemKey);

    return Result(HttpStatusCodes.Created);
  }

  private sendSystemAccountInvalidation(gameId: number, systemKey: string) {
    this.gameEvents.dispatchEvent(
      new GameEvent({
        gameId,
        target: [SystemAccountsResource.route, systemKey],
      })
    );
  }
}